import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Sun, Moon, Monitor, Save } from 'lucide-react';
import { applyTheme, getSavedTheme, type ThemeValue } from '@/hooks/useTheme';

interface PreferencesData {
  theme: ThemeValue;
  notifications_email: boolean;
  notifications_push: boolean;
  notifications_marketing: boolean;
  reduced_motion: boolean;
}

const THEME_OPTIONS: { id: ThemeValue; name: string; icon: typeof Sun; desc: string }[] = [
  { id: 'light', name: 'Claro', icon: Sun, desc: 'Interface branca, adequada para ambientes iluminados.' },
  { id: 'dark', name: 'Escuro', icon: Moon, desc: 'Dark mode permanente — padrão da plataforma.' },
  { id: 'system', name: 'Sistema', icon: Monitor, desc: 'Segue automaticamente o tema do seu sistema operacional.' },
];

export function ProfilePreferences() {
  const queryClient = useQueryClient();

  // Inicializa do localStorage para resposta imediata
  const [theme, setThemeState] = useState<ThemeValue>(getSavedTheme);
  const [emailNotify, setEmailNotify] = useState(true);
  const [pushNotify, setPushNotify] = useState(true);
  const [marketingNotify, setMarketingNotify] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Aplica tema no DOM imediatamente ao clicar — sem esperar o banco
  const handleThemeClick = (t: ThemeValue) => {
    setThemeState(t);
    applyTheme(t);
  };

  const { data: prefs, isLoading } = useQuery<PreferencesData>({
    queryKey: ['user-preferences'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data, error } = await supabase
        .from('user_preferences')
        .select('theme, notifications_email, notifications_push, notifications_marketing, reduced_motion')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data as PreferencesData;
    },
  });

  // Quando os dados chegam do banco, sincroniza o estado
  useEffect(() => {
    if (prefs) {
      setThemeState(prefs.theme);
      applyTheme(prefs.theme); // aplica o tema salvo no banco
      setEmailNotify(prefs.notifications_email);
      setPushNotify(prefs.notifications_push);
      setMarketingNotify(prefs.notifications_marketing);
      setReducedMotion(prefs.reduced_motion);
    }
  }, [prefs]);

  const mutation = useMutation({
    mutationFn: async (updated: PreferencesData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('user_preferences')
        .upsert({ user_id: user.id, ...updated, updated_at: new Date().toISOString() });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Preferências salvas!');
      void queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
    },
    onError: () => {
      toast.error('Erro ao salvar preferências.');
    },
  });

  const handleSave = () => {
    mutation.mutate({
      theme,
      notifications_email: emailNotify,
      notifications_push: pushNotify,
      notifications_marketing: marketingNotify,
      reduced_motion: reducedMotion,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 rounded-xl bg-midnight-4/50" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* ── Tema visual ── */}
      <section className="space-y-4">
        <div>
          <h3 className="text-base font-bold text-ink">Tema do Painel</h3>
          <p className="text-xs text-ink-muted mt-0.5">
            Clique para aplicar imediatamente. Salve para persistir.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {THEME_OPTIONS.map((mode) => {
            const Icon = mode.icon;
            const isSelected = theme === mode.id;
            return (
              <button
                key={mode.id}
                type="button"
                onClick={() => handleThemeClick(mode.id)}
                title={mode.desc}
                className={`group flex flex-col items-center gap-2.5 rounded-xl border p-4 text-center transition-all duration-base cursor-pointer ${
                  isSelected
                    ? 'border-accent bg-accent/10 text-accent shadow-glow-xs'
                    : 'border-hairline bg-midnight-3/60 text-ink-muted hover:border-accent/40 hover:text-ink'
                }`}
              >
                <div className={`relative flex h-10 w-10 items-center justify-center rounded-xl transition-all ${
                  isSelected ? 'bg-accent/20' : 'bg-midnight-4/60 group-hover:bg-midnight-4'
                }`}>
                  {isSelected && (
                    <div className="absolute inset-0 rounded-xl blur-lg bg-accent/30" />
                  )}
                  <Icon className="relative h-5 w-5" />
                </div>
                <span className="text-xs font-semibold">{mode.name}</span>
                {isSelected && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-accent opacity-80">
                    Ativo
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Acessibilidade ── */}
      <section className="space-y-4 border-t border-hairline/60 pt-6">
        <div>
          <h3 className="text-base font-bold text-ink">Acessibilidade</h3>
          <p className="text-xs text-ink-muted mt-0.5">Comportamentos de animação e movimento.</p>
        </div>

        <ToggleRow
          label="Reduzir Movimento"
          desc="Desativa animações e transições pesadas em toda a interface."
          checked={reducedMotion}
          onChange={() => setReducedMotion((v) => !v)}
        />
      </section>

      {/* ── Notificações ── */}
      <section className="space-y-4 border-t border-hairline/60 pt-6">
        <div>
          <h3 className="text-base font-bold text-ink">Notificações</h3>
          <p className="text-xs text-ink-muted mt-0.5">Canais de recebimento de atualizações.</p>
        </div>

        <div className="space-y-2">
          <ToggleRow
            label="E-mails de Atividades"
            desc="Alertas importantes enviados ao seu e-mail cadastrado."
            checked={emailNotify}
            onChange={() => setEmailNotify((v) => !v)}
          />
          <ToggleRow
            label="Notificações Push"
            desc="Notificações instantâneas no desktop ou dispositivo móvel."
            checked={pushNotify}
            onChange={() => setPushNotify((v) => !v)}
          />
          <ToggleRow
            label="Novidades & Marketing"
            desc="Insights semanais, dicas de uso e novidades da plataforma."
            checked={marketingNotify}
            onChange={() => setMarketingNotify((v) => !v)}
          />
        </div>
      </section>

      {/* ── Salvar ── */}
      <div className="flex justify-end border-t border-hairline/60 pt-5">
        <button
          onClick={handleSave}
          disabled={mutation.isPending}
          className="flex items-center gap-2 rounded-xl btn-accent px-6 py-3 text-sm cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Save className="h-4 w-4" />
          <span>{mutation.isPending ? 'Salvando...' : 'Salvar Preferências'}</span>
        </button>
      </div>
    </div>
  );
}

/* ── Toggle genérico ── */
interface ToggleRowProps {
  label: string;
  desc: string;
  checked: boolean;
  onChange: () => void;
}

function ToggleRow({ label, desc, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-hairline bg-midnight-2/40 p-4">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">{label}</p>
        <p className="text-xs text-ink-muted leading-relaxed mt-0.5">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          checked ? 'bg-accent' : 'bg-midnight-5'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}
